/**
 * Pure, dependency-free graph parse + layout for the Linked Wheels Map
 * (SDD-24 / WL-052b, map piece). No React, no DOM, no network — fully
 * unit-tested in graphLayout.test.js.
 *
 * The map is a compact ego-graph: the current wheel sits at the centre and its
 * linked wheels fan out in concentric rings by hop distance. parseGraph()
 * normalises the tolerant/varying shape the /wheel/{id}/graph/ endpoint may
 * return; layoutGraph() turns a normalised graph into deterministic pixel
 * coordinates the component renders (WheelThumb nodes + SVG connector lines).
 *
 * Kept pure so the layout maths (radii, ring distribution, overflow "+N",
 * edge resolution, synthetic centre connectivity) is testable without a browser.
 */
import { isTombstone } from "./logic";

/** Normalise one raw node into the shape the map renders. Tombstones never leak a name. */
export function normalizeNode(raw) {
  const n = raw && typeof raw === "object" ? raw : {};
  const tombstone = isTombstone(n);
  const hopRaw = n.hop != null ? n.hop : n.depth;
  const hop = Number.isFinite(Number(hopRaw)) ? Number(hopRaw) : 1;
  return {
    id: n.id != null ? n.id : null,
    // Never render a removed/private wheel's name or photo (visibility leak).
    name: tombstone ? "" : n.name || "",
    portrait: tombstone ? null : n.portrait || n.thumbnail || null,
    ownership: n.ownership || "other",
    hop,
    tombstone,
    label: n.label || "",
  };
}

/**
 * Alias the backend node keys onto the ones the normaliser/layout expect. The
 * /graph/ (and /creator/<id>/graph/) endpoint emits { wheel_id, title, thumbnail,
 * ownership, is_center }; downstream code keys off id/name/portrait. Aliasing
 * once here keeps the rest tolerant. (Without this the real payload's ids read as
 * null and EVERY node is dropped — the map renders empty even when mounted.)
 */
function aliasRawNode(n) {
  if (!n || typeof n !== "object") return n;
  return {
    ...n,
    id: n.id != null ? n.id : n.wheel_id,
    name: n.name != null ? n.name : n.title,
  };
}

/**
 * Normalise the raw /graph/ payload into { center, nodes, edges }.
 * Tolerant to field aliases (wheel_id vs id, title vs name, from/to vs
 * source/target, thumbnail vs portrait, center inside nodes vs a top-level
 * center, missing center -> fallbackId).
 */
export function parseGraph(raw, fallbackId = null) {
  const r = raw && typeof raw === "object" ? raw : {};
  const rawNodes = (Array.isArray(r.nodes) ? r.nodes : []).map(aliasRawNode);

  let center = r.center && typeof r.center === "object" ? r.center : null;
  if (!center) {
    center =
      rawNodes.find((n) => n && (n.is_center || n.center)) ||
      (fallbackId != null ? { id: fallbackId } : rawNodes[0] || { id: null });
  }
  const centerId = center && center.id != null ? center.id : fallbackId;

  const seen = new Set();
  const nodes = [];
  for (const n of rawNodes) {
    if (!n || n.id == null || n.id === centerId || seen.has(n.id)) continue;
    seen.add(n.id);
    nodes.push(normalizeNode(n));
  }

  const rawEdges = Array.isArray(r.edges) ? r.edges : [];
  const edges = rawEdges
    .map((e) => ({
      source: e.source != null ? e.source : e.from,
      target: e.target != null ? e.target : e.to,
      label: e.label || "",
      reciprocal: !!(e.reciprocal || e.is_reciprocal),
    }))
    .filter((e) => e.source != null && e.target != null);

  return {
    center: normalizeNode({ ...center, id: centerId }),
    nodes,
    edges,
  };
}

const DEFAULTS = {
  width: 360,
  height: 320,
  nodeSize: 44,
  centerSize: 56,
  maxPerRing: 10,
  pad: 10,
};

/**
 * Lay out a normalised graph into pixel coordinates.
 *
 * @returns {{
 *   center: {id, name, portrait, x, y, size, tombstone},
 *   placed: Array<{id, name, portrait, ownership, ring, x, y}>,
 *   overflow: Array<{ring, count, x, y}>,
 *   edges: Array<{source, target, x1, y1, x2, y2, reciprocal, synthetic}>,
 *   width: number, height: number, nodeSize: number, centerSize: number
 * }}
 */
export function layoutGraph(graph, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  const g = graph || { center: normalizeNode({}), nodes: [], edges: [] };
  const center = g.center || normalizeNode({});
  const nodes = Array.isArray(g.nodes) ? g.nodes : [];
  const edges = Array.isArray(g.edges) ? g.edges : [];

  const W = Math.max(o.width, 120);
  const H = Math.max(o.height, 120);
  const cx = W / 2;
  const cy = H / 2;
  const nodeR = o.nodeSize / 2;

  // Group linked nodes into concentric rings by hop.
  const byRing = new Map();
  for (const n of nodes) {
    const ring = Math.max(1, Math.round(n.hop || 1));
    if (!byRing.has(ring)) byRing.set(ring, []);
    byRing.get(ring).push(n);
  }
  const ringKeys = [...byRing.keys()].sort((a, b) => a - b);
  const ringCount = ringKeys.length || 1;

  const maxRadius = Math.max(nodeR + 4, Math.min(W, H) / 2 - nodeR - o.pad);
  const minRadius = Math.min(maxRadius, Math.max(o.centerSize / 2 + nodeR + 8, maxRadius * 0.5));
  const radiusFor = (idx) => {
    if (ringCount === 1) return maxRadius;
    return minRadius + (maxRadius - minRadius) * (idx / (ringCount - 1));
  };

  const placed = [];
  const overflow = [];

  ringKeys.forEach((ringKey, idx) => {
    const list = byRing.get(ringKey);
    const hasOverflow = list.length > o.maxPerRing;
    const shown = hasOverflow ? list.slice(0, o.maxPerRing) : list;
    const overflowCount = hasOverflow ? list.length - o.maxPerRing : 0;
    const slots = shown.length + (overflowCount > 0 ? 1 : 0);
    const r = radiusFor(idx);
    // Start at the top; stagger each ring so radial lines never overlap.
    const angleOffset = -Math.PI / 2 + idx * 0.5;
    const angleStep = (2 * Math.PI) / Math.max(slots, 1);

    shown.forEach((n, i) => {
      const a = angleOffset + i * angleStep;
      placed.push({
        ...n,
        ring: ringKey,
        x: cx + r * Math.cos(a),
        y: cy + r * Math.sin(a),
      });
    });
    if (overflowCount > 0) {
      const a = angleOffset + shown.length * angleStep;
      overflow.push({
        ring: ringKey,
        count: overflowCount,
        x: cx + r * Math.cos(a),
        y: cy + r * Math.sin(a),
      });
    }
  });

  // Resolve edges to coordinates; drop any endpoint we didn't place.
  const posById = new Map();
  posById.set(center.id, { x: cx, y: cy });
  placed.forEach((p) => posById.set(p.id, { x: p.x, y: p.y }));

  const resolved = [];
  const touched = new Set();
  for (const e of edges) {
    const s = posById.get(e.source);
    const t = posById.get(e.target);
    if (!s || !t) continue;
    touched.add(e.source);
    touched.add(e.target);
    resolved.push({
      source: e.source,
      target: e.target,
      x1: s.x,
      y1: s.y,
      x2: t.x,
      y2: t.y,
      reciprocal: !!e.reciprocal,
      synthetic: false,
    });
  }

  // Every placed node must be visually connected: if the payload gave no edge
  // touching a node (or no edges at all), draw a centre -> node connector so a
  // node is never an orphan floating disc.
  for (const p of placed) {
    if (!touched.has(p.id)) {
      resolved.push({
        source: center.id,
        target: p.id,
        x1: cx,
        y1: cy,
        x2: p.x,
        y2: p.y,
        reciprocal: false,
        synthetic: true,
      });
    }
  }

  return {
    center: { ...center, x: cx, y: cy, size: o.centerSize },
    placed,
    overflow,
    edges: resolved,
    width: W,
    height: H,
    nodeSize: o.nodeSize,
    centerSize: o.centerSize,
  };
}
